import { type ActionContext } from 'vuex';

import {
  getAgencies,
  getAgencyData,
  getAgencyDetailsById,
  getAgencyUsers,
  getIsBillToAgency,
} from '@/api/agency';
import { getBatchUnreadMessageCounts, type UnreadMessageCount } from '@/api/flatfox';
import { getLatestImport, type LatestImportResponse } from '@/api/importReport';
import { fetchToplistingCreditUsage, type ToplistingCreditUsage } from '@/api/toplistingService';
import auth0 from '@/auth/auth0Plugin';
import type { UserData } from '@/components/Agency/UserFormData/types';
import { agencyHasSomeProductCodes } from '@/helpers/agencyProductCodes';
import { mapUtcToHgRetsDateFormat } from '@/helpers/dateUtils';
import type { AccountOwner, AgencyDetails, AgencyProduct } from '@/types-agency';
import { AgencyProductCodes } from '@/types-agency';
import { captureError } from '@smg-real-estate/browser-rum';
import { OfferType, RankType } from '@smg-real-estate/hg-rets-schema';
import clone from 'lodash.clone';

export interface AgencyState {
  activeAgencyId?: string;
  agencies: AgencyDetails[];
  activeProducts: AgencyProduct[];
  usersForActiveAgency: UserData[];
  isBillToAgency: boolean;
  isImmoCreditsCustomer: boolean;
  rating: string;
  salesRegion: string;
  accountOwner: AccountOwner;
  bcsOwner?: AccountOwner;
  flatfoxUnreadMessageCounts: UnreadMessageCount[];
  importSummary: LatestImportResponse | null;
}

export interface Options {
  userId: string;
  requiredMFA: boolean;
}

const LOCALSTORAGE_CUSTOMER_ID_KEY = 'customer_id';

const hasImmoCredits = (payload: ToplistingCreditUsage | undefined): boolean =>
  !!payload &&
  [
    payload.initialCreditInCHFCents,
    payload.availableCreditInCHFCents,
    payload.usedCreditInCHFCents,
    payload.nextCreditInCHFCents,
  ].some((creditAmount) => creditAmount > 0);

// Default owner information for accounts with 'Mass Market' rating
const MASS_MARKET_ACCOUNT_OWNER = {
  email: 'customerservice-re@swissmarketplace.group',
  ownerName: 'Deborah Spahr',
  phone: '+41589007308',
};

// Default Romandie owner information for accounts with 'Mass Market' rating
const ROMANDIE_MASS_MARKET_ACCOUNT_OWNER = {
  email: 'romandie@swissmarketplace.group',
  ownerName: 'René Bärtschi',
  phone: '+41215468780',
};

const BCS_OWNER_FALLBACK = {
  email: 'customerservice-re@swissmarketplace.group',
  ownerName: '',
  phone: '+41 58 900 73 08',
};

const romandieRegions: string[] = ['Romandie', 'Romandie Ouest & Est', 'Romandie Centre'];

function getInitialState(): AgencyState {
  return {
    activeAgencyId: undefined,
    agencies: [],
    activeProducts: [],
    usersForActiveAgency: [],
    isBillToAgency: false,
    isImmoCreditsCustomer: false,
    rating: '',
    salesRegion: '',
    accountOwner: {
      email: '',
      ownerName: '',
      phone: '',
    },
    flatfoxUnreadMessageCounts: [],
    importSummary: null,
  };
}

const getters = {
  getActiveAgency: (state: AgencyState): AgencyDetails | undefined =>
    state.agencies.find((agency) => agency.customerId === state.activeAgencyId),
  getAgencies(state: AgencyState): AgencyDetails[] {
    return state.agencies;
  },
  getActiveProducts(state: AgencyState): Array<AgencyProduct> {
    return state.activeProducts;
  },
  getIsFlatfoxActive: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.FLATFOX_INTEGRATION], state),
  getFlatfoxUnreadMessageCountForInsertion:
    (state: AgencyState) =>
    (insertionId: string): number => {
      const unreadMessageCount = state.flatfoxUnreadMessageCounts?.find(
        (count) => count.smg_listing === insertionId,
      );
      return unreadMessageCount?.count ?? 0;
    },
  getIsFlatfoxApplicationFormActive: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.FLATFOX_INTEGRATION], state) &&
    agencyHasSomeProductCodes(
      [
        AgencyProductCodes.WELCOME,
        AgencyProductCodes.EXPERIENCED,
        AgencyProductCodes.PROFESSIONAL,
        AgencyProductCodes.EXPERT,
        AgencyProductCodes.SME,
      ],
      state,
    ),
  getIsCasaleadActive: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes(
      [
        AgencyProductCodes.CASALEAD_DE,
        AgencyProductCodes.CASALEAD_FR,
        AgencyProductCodes.CASALEAD_IT,
      ],
      state,
    ),
  getIsCasaleadPlusActive: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes(
      [AgencyProductCodes.EXPERT_DE, AgencyProductCodes.EXPERT_FR, AgencyProductCodes.EXPERT_IT],
      state,
    ),
  /**
   * AgencyProductCodes.EXPERT = GTM26 Gold
   * AgencyProductCodes.PROFESSIONAL = GTM26 Silver
   * AgencyProductCodes.EXPERIENCED = GTM26 Bronze
   * AgencyProductCodes.SME = GTM26 Flex
   */
  getIsGoldPackage: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.EXPERT], state),
  getIsWelcomePackage: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.WELCOME], state),
  getIsSilverPackage: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.PROFESSIONAL], state),
  getIsBronzePackage: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.EXPERIENCED], state),
  getIsSMEPackage: (state: AgencyState): boolean =>
    agencyHasSomeProductCodes([AgencyProductCodes.SME], state),

  getFlatfoxAgencyId: (state: AgencyState): string => {
    const [product] = state.activeProducts.filter(
      (ap: AgencyProduct) => ap.productCode === AgencyProductCodes.FLATFOX_INTEGRATION,
    );

    return product?.agencyId;
  },
  getAgencyById:
    (state: AgencyState) =>
    (customerId: string): AgencyDetails | undefined =>
      state.agencies.find((agency) => agency.customerId === customerId),
  getActiveAgencyId: (state: AgencyState): string | undefined => state.activeAgencyId,
  getActiveAgencyPersonId: (state: AgencyState): string | undefined =>
    state.agencies.find((agency) => agency.customerId === state.activeAgencyId)?.personId,
  getAgenciesWithoutSelectedAgency: (state: AgencyState): AgencyDetails[] =>
    state.agencies.filter((agency) => agency.customerId !== state.activeAgencyId) || [],
  getUsersForActiveAgency: (state: AgencyState): UserData[] => state.usersForActiveAgency,
  getIsBillToAgency: (state: AgencyState): boolean => state.isBillToAgency,
  getIsImmoCreditsCustomer: (state: AgencyState): boolean => state.isImmoCreditsCustomer,
  getAccountOwner: (state: AgencyState): AccountOwner => {
    if (state.rating === 'Mass Market') {
      return romandieRegions.includes(state.salesRegion)
        ? ROMANDIE_MASS_MARKET_ACCOUNT_OWNER
        : MASS_MARKET_ACCOUNT_OWNER;
    }
    return state.accountOwner;
  },
  getBcsOwner: (state: AgencyState): AccountOwner => ({
    email: state.bcsOwner?.email || BCS_OWNER_FALLBACK.email,
    ownerName: state.bcsOwner?.ownerName || BCS_OWNER_FALLBACK.ownerName,
    phone: state.bcsOwner?.phone || BCS_OWNER_FALLBACK.phone,
  }),
  getImportSummary: (state: AgencyState): LatestImportResponse | null => state.importSummary,
  getHasImportData: (state: AgencyState): boolean => state.importSummary !== null,
};

const mutations = {
  setActiveAgencyId(state: AgencyState, activeAgencyId: string): void {
    state.activeAgencyId = activeAgencyId;
  },
  setUsersForActiveAgency(state: AgencyState, users: []): void {
    state.usersForActiveAgency = users;
  },
  setUpdatedRequiredMfaForActiveAgencyUser(state: AgencyState, options: Options): void {
    const { userId, requiredMFA } = options;
    const clonedUsers = clone(state.usersForActiveAgency);
    clonedUsers.forEach((userData: UserData) => {
      if (userData.userId === userId) {
        userData.requiredMFA = requiredMFA;
      }
    });
    state.usersForActiveAgency = clonedUsers;
  },
  setAgencies(state: AgencyState, payload: AgencyDetails[]): void {
    state.agencies = payload;
  },
  addAgency(state: AgencyState, agency: AgencyDetails): void {
    state.agencies.push(agency);
  },
  setActiveProducts(state: AgencyState, payload: Array<AgencyProduct>): void {
    state.activeProducts = payload;
  },
  setAccountOwner(
    state: AgencyState,
    payload: { rating: string; salesRegion: string; accountOwner: AccountOwner },
  ): void {
    state.accountOwner = payload.accountOwner || MASS_MARKET_ACCOUNT_OWNER;
    state.rating = payload.rating;
    state.salesRegion = payload.salesRegion;
  },
  setBcsOwner(state: AgencyState, payload: AccountOwner | undefined): void {
    state.bcsOwner = payload;
  },
  setLogoUrlAsEmpty(state: AgencyState, customerId: string): void {
    const brokenCustomer = state.agencies.find((agency) => agency.customerId === customerId);
    if (brokenCustomer) {
      brokenCustomer.logoUrl = undefined;
    }
  },
  setIsBillToAgency(state: AgencyState, isBillToAgency: boolean): void {
    state.isBillToAgency = isBillToAgency;
  },
  setIsImmoCreditsCustomer(state: AgencyState, isImmoCreditsCustomer: boolean): void {
    state.isImmoCreditsCustomer = isImmoCreditsCustomer;
  },
  setFlatfoxUnreadMessageCounts(
    state: AgencyState,
    flatfoxUnreadMessageCounts: UnreadMessageCount[],
  ): void {
    // Merge new counts into existing array to avoid losing data from other listings
    const existingMap = new Map(state.flatfoxUnreadMessageCounts.map((c) => [c.smg_listing, c]));
    flatfoxUnreadMessageCounts.forEach((count) => {
      existingMap.set(count.smg_listing, count);
    });
    state.flatfoxUnreadMessageCounts = Array.from(existingMap.values());
  },
  setImportSummary(state: AgencyState, importSummary: LatestImportResponse | null): void {
    state.importSummary = importSummary;
  },
};

const actions = {
  setActiveAgencyIdToLocalStorage(
    context: ActionContext<AgencyState, any>,
    activeAgencyId?: string,
  ): void {
    if (!activeAgencyId) {
      localStorage.removeItem(LOCALSTORAGE_CUSTOMER_ID_KEY);
      return;
    }
    localStorage.setItem(LOCALSTORAGE_CUSTOMER_ID_KEY, activeAgencyId);
  },
  markAgencyLogoAsBroken(context: ActionContext<AgencyState, any>, customerId: string): void {
    context.commit('setLogoUrlAsEmpty', customerId);
  },
  getAgencyFromLocalStorage(): string | null {
    return localStorage.getItem(LOCALSTORAGE_CUSTOMER_ID_KEY);
  },
  async setActiveAgencyId(
    context: ActionContext<AgencyState, any>,
    customerId: string,
  ): Promise<void> {
    context.commit('setActiveAgencyId', customerId);
    await context.dispatch('setActiveAgencyIdToLocalStorage', customerId);
  },
  async getAgencies(context: ActionContext<AgencyState, any>): Promise<void> {
    try {
      const userId = context.getters.getAuth0Id;
      const payload = await getAgencies(userId);

      context.commit('setAgencies', payload);
    } catch (e) {
      captureError(e);
      context.commit('setAgencies', []);
    }
  },
  async getAgencyById(
    context: ActionContext<AgencyState, any>,
    agencyId: string,
  ): Promise<AgencyDetails | null> {
    const newAgency = await getAgencyDetailsById(agencyId);
    if (newAgency) {
      context.commit('addAgency', newAgency);
    }
    return newAgency;
  },
  async fetchAndSetUsersForAgency(
    context: ActionContext<AgencyState, any>,
    agencyId: string,
  ): Promise<void> {
    const users = await getAgencyUsers(agencyId);
    context.commit('setUsersForActiveAgency', users);
  },
  /**
   * Fetch active products and account owner from NIF
   * @param context - Conxtext
   * @param customerId - AgencyID
   */
  async getAgencyProductsAndOwner(
    context: ActionContext<AgencyState, any>,
    customerId: string,
  ): Promise<void> {
    const agencyData = await getAgencyData(customerId);
    if (!agencyData) {
      console.warn('Cannot fetch agency data for', customerId);
    }
    context.commit('setActiveProducts', agencyData?.activeProducts || []);
    context.commit('setBcsOwner', agencyData?.bcsOwner);
    context.commit('setAccountOwner', {
      rating: agencyData?.rating,
      salesRegion: agencyData?.salesRegion,
      accountOwner: agencyData?.accountOwner,
    });
  },
  async switchToAgency(
    context: ActionContext<AgencyState, any>,
    requestedAgencyId?: string,
  ): Promise<AgencyDetails | null> {
    const agencyDetails = await context.dispatch('resolveAgency', requestedAgencyId);
    if (!agencyDetails) {
      return null;
    }

    const agencyId = agencyDetails.customerId;
    await context.dispatch('getAgencyProductsAndOwner', agencyId);

    // Refresh token to access selected agency
    await auth0.getAccessTokenSilently({
      cacheMode: 'off',
      authorizationParams: {
        selectedCustomerId: agencyId,
        selectedPersonId: agencyDetails?.personId,
      },
    });

    // set in store and local storage
    await context.dispatch('setActiveAgencyId', agencyId);

    context.dispatch('fetchAgencyLeads', undefined, { root: true });

    await context.dispatch('agencyProfile/fetchAgencyProfile');

    // trigger fetching of insertions
    context.commit('setInsertionsAsChanged', true);

    // Fetches a flag used for downloading cost or billing report
    // NOTE: can it be rolled into some other call? Likely not, since Listing Quality API has this data
    context.dispatch('getIsBillToAgency');

    // Gets top-listing credits
    context.dispatch('getIsImmoCreditsCustomer');

    // Fetch import summary for CRM upload report
    context.dispatch('fetchImportSummary');

    return agencyDetails;
  },
  async resolveAgency(
    context: ActionContext<AgencyState, any>,
    requestedAgencyId?: string,
  ): Promise<AgencyDetails | null> {
    const agencyId = requestedAgencyId || (await context.dispatch('retrieveLastAgencyId'));
    if (context.getters.hasCCCGroup) {
      return (
        context.getters.getAgencyById(agencyId) ||
        (await context.dispatch('getAgencyById', agencyId))
      );
    } else {
      return context.getters.getAgencyById(agencyId) || context.getters.getAgencies[0];
    }
  },
  async retrieveLastAgencyId(context: ActionContext<AgencyState, any>): Promise<string> {
    return (
      context.getters.getActiveAgencyId || (await context.dispatch('getAgencyFromLocalStorage'))
    );
  },
  async getIsBillToAgency(context: ActionContext<AgencyState, any>): Promise<void> {
    const userIsNotAssignedToAnAgency = context.getters.getActiveAgencyId === undefined;

    if (userIsNotAssignedToAnAgency) return;

    try {
      const payload = await getIsBillToAgency();

      context.commit('setIsBillToAgency', payload);
    } catch (e) {
      captureError(e);
    }
  },
  async getIsImmoCreditsCustomer(context: ActionContext<AgencyState, any>): Promise<void> {
    const userIsNotAssignedToAnAgency = context.getters.getActiveAgencyId === undefined;

    if (userIsNotAssignedToAnAgency) {
      context.commit('setIsImmoCreditsCustomer', false);
      return;
    }

    context.commit('setIsImmoCreditsCustomer', false);

    try {
      const payload = await fetchToplistingCreditUsage(
        OfferType.Rent,
        RankType.Top,
        mapUtcToHgRetsDateFormat(new Date().toISOString()),
        null,
      );

      context.commit('setIsImmoCreditsCustomer', hasImmoCredits(payload));
    } catch (e) {
      captureError(e);
    }
  },
  async fetchFlatfoxUnreadMessageCounts(
    context: ActionContext<AgencyState, any>,
    listingIds: string[],
  ): Promise<void> {
    if (!listingIds.length) {
      context.commit('setFlatfoxUnreadMessageCounts', []);
      return;
    }

    const customerId = context.getters.getActiveAgencyId;
    if (!customerId) {
      context.commit('setFlatfoxUnreadMessageCounts', []);
      return;
    }

    try {
      const flatfoxUnreadMessageCounts = await getBatchUnreadMessageCounts(listingIds, customerId);
      context.commit('setFlatfoxUnreadMessageCounts', flatfoxUnreadMessageCounts);
    } catch (e) {
      captureError(e);
      // Set empty array on error to avoid showing stale data
      context.commit('setFlatfoxUnreadMessageCounts', []);
    }
  },
  async fetchImportSummary(context: ActionContext<AgencyState, any>): Promise<void> {
    try {
      const importData = await getLatestImport(false);
      context.commit('setImportSummary', importData);
    } catch {
      context.commit('setImportSummary', null);
    }
  },
};

const _default = { state: getInitialState, getters, mutations, actions };
export default _default;
